import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '~/api-client';
import { UnitLocationBootstrapResponse, UnitLocationResource } from '@mark43/cad-api';
import { createStreamListener, useStream } from '../../socket/use-stream';
import { useEnabledFeatures } from './enabled-features/use-enabled-features';
import { useEffect, useMemo, useState } from 'react';
import { useIntegratedStreams } from './use-integrated-streams';

export function useStreamUnitLocation({
    unitId,
    enabled = true,
}: {
    unitId?: number;
    enabled?: boolean;
}) {
    const apiClient = useApiClient();
    const { unitLocationStreamEnabled } = useEnabledFeatures();
    const [unitLocationData, setUnitLocationData] = useState<
        undefined | UnitLocationBootstrapResponse
    >(undefined);

    const query = useQuery(
        ['unit-location-view', unitId],
        () => {
            if (!unitId) {
                return;
            }

            return apiClient
                .request<UnitLocationResource.BootstrapUnitLocation>({
                    method: 'POST',
                    url: 'unit_location/bootstrap',
                    data: { unitId },
                })
                .then((resp) => resp.data?.data);
        },
        { enabled }
    );

    useIntegratedStreams({
        enabled: enabled && unitLocationStreamEnabled,
        streamNames: ['unitLocationBundles'],
        query,
        onMessage(message) {
            if (message.payload) {
                setUnitLocationData(message.payload);
            }
        },
    });

    const streamListener = useMemo(() => {
        return createStreamListener({
            streamNames: ['nudge'],
            onMessage: (message) => {
                if (unitLocationStreamEnabled) return;
                if (message.payload.map?.['ULI']) {
                    query.refetch();
                }
            },
        });
    }, [query, unitLocationStreamEnabled]);

    useStream(streamListener);

    const unitLocationBootstrapResponse = query.data;

    useEffect(() => {
        setUnitLocationData(unitLocationBootstrapResponse);
    }, [unitLocationBootstrapResponse]);

    return unitLocationData;
}
